import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { Spinner } from '@blueprintjs/core'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

import {
  IS_REJECTED_UI_ERROR,
  IS_REQUIRED_UI_ERROR,
  IRejectionValue,
  filterUiErrors,
  isRequiredEmpty,
} from 'shared-libs/components/ui2/fields/core-field/bound-field-props'
import { resolveBoundFieldPath } from 'shared-libs/components/ui2/fields/core-field/resolve-field-path'
import { UI2FormSectionSubmitContext } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-submit-context'
import {
  PrefilledFieldState,
  createPrefilledFieldState,
  beginPrefilledEdit,
  cancelPrefilledEdit,
  hasFieldValue,
} from 'shared-libs/components/ui2/fields/core-field/bound-field-state'
import {
  FieldShellProps,
  IFieldShellEditableExtras,
} from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-props'
import {
  FieldShellContext,
  IFieldShellContext,
} from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-context'
import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { UI2FormSectionIdContext } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-id-context'
import {
  IUI2WizardContext,
  UI2WizardContext,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-wizard-context'

import { UI2Label } from '../label/label'
import { UI2NoValueAvailable } from '../no-value-available/no-value-available'
import {
  UI2PrefilledCancelButton,
  UI2PrefilledEditButton,
} from '../prefilled-button/prefilled-button'
import { IUI2PulseHighlightRef, UI2PulseHighlight } from '../../../behavior/pulse-highlight/pulse-highlight'

import './_field-shell.scss'

interface IUI2FieldShellWebExtras extends Omit<IBaseProps, 'children'> {
  frames?: FramesManager
  translationTable?: any
  isHorizontalLayout?: boolean
}

export interface IUI2FieldShellProps<T = any>
  extends FieldShellProps<T>,
    IUI2FieldShellWebExtras {}

interface IUI2FieldShellState<T> {
  prefilled: PrefilledFieldState<T>
}

const FieldShellContextProvider = FieldShellContext.Provider as React.ComponentType<{
  value: IFieldShellContext
  children?: React.ReactNode
}>

export class UI2FieldShell<T = any> extends React.Component<
  IUI2FieldShellProps<T>,
  IUI2FieldShellState<T>
> {
  public static contextType = FormFocusTargetContext as any
  declare context: IFormFocusTargetContext<HTMLElement> | undefined

  // Read via Consumers inside render (class components support only one contextType).
  private currentSectionId: string | undefined
  private currentWizardCtx: IUI2WizardContext | undefined

  private outerRef = React.createRef<HTMLDivElement>()
  private pulseRef = React.createRef<IUI2PulseHighlightRef>()
  private registeredPath: string | undefined
  private showRequiredErrors = false

  constructor(props: IUI2FieldShellProps<T>) {
    super(props)
    this.state = {
      prefilled: createPrefilledFieldState(props.value, props.enablePrefilledMode ?? false),
    }
  }

  public componentDidMount(): void {
    this.syncScrollTargetRegistration()
    const { initialValue, value, onChange } = this.props
    if (initialValue !== undefined && !hasFieldValue(value) && onChange) {
      onChange(initialValue)
    }
  }

  public componentWillUnmount(): void {
    if (this.registeredPath && this.context?.unregister) {
      this.context.unregister(this.registeredPath)
    }
    this.registeredPath = undefined
  }

  public componentDidUpdate(prevProps: IUI2FieldShellProps<T>): void {
    this.syncScrollTargetRegistration()
    this.clearRejectionIfResolved(prevProps.value)
    if (!this.isPrefilledDisplay()) return
    const isEmpty = this.resolveIsEmpty()
    if (!isEmpty(prevProps.value) && isEmpty(this.props.value)) {
      this.setState(s => ({
        prefilled: { ...s.prefilled, originalValue: undefined, isEditing: true },
      }))
    }
  }

  private clearRejectionIfResolved(prevValue: T | undefined): void {
    const { value, rejectionPath, onChange } = this.props
    if (!rejectionPath || !onChange) return
    const rejection = this.getRejectionValue()
    if (rejection?.status !== 'rejected') return
    const isEmpty = this.resolveIsEmpty()
    const prevWasUnchanged = isEmpty(prevValue) || _.isEqual(prevValue, rejection.originalValue)
    const nowResolved = !isEmpty(value) && !_.isEqual(value, rejection.originalValue)
    if (prevWasUnchanged && nowResolved) {
      // Use onChange with the overridePath arg the entity renderer supports, so
      // the write goes through the same handleChange path as any field edit.
      ;(onChange as any)(undefined, false, rejectionPath)
    }
  }

  private syncScrollTargetRegistration(): void {
    const nextPath = this.getFieldPath()
    if (nextPath === this.registeredPath) return
    if (this.registeredPath && this.context?.unregister) {
      this.context.unregister(this.registeredPath)
    }
    this.registeredPath = nextPath
    if (nextPath && this.context?.register) {
      this.context.register(nextPath, {
        getRef: () => this.outerRef.current,
        pulse: (intent) => this.getPulseTarget()?.pulse(intent),
        hasError: () => !_.isEmpty(this.getEffectiveErrors()),
        sectionId: this.currentSectionId,
        revealSection: this.revealSection,
      })
    }
  }

  // Reveal this field's section in the wizard when it isn't already
  // active — works forward as well as backward (uses `revealSection`,
  // not `jumpTo`, so a save-flow submit can jump to a not-yet-visited
  // section). The provider mounts above UI2Form and can't see the
  // wizard context, so the field owns this handoff.
  private revealSection = (): void => {
    const ctx = this.currentWizardCtx
    const id = this.currentSectionId
    if (!ctx?.isWizard || !id) return
    if (ctx.activeSectionId === id) return
    ctx.revealSection(id)
  }

  private resolveIsEmpty(): (v: T | undefined) => boolean {
    return this.props.isValueEmpty ?? ((v) => !hasFieldValue(v))
  }

  private isPrefilledDisplay(): boolean {
    if (this.getRejectionValue()?.status === 'rejected') return false
    const { prefilled } = this.state
    return prefilled.originalValue !== undefined && !prefilled.isEditing
  }

  private getEffectiveReadOnly(): boolean {
    return (this.props.isReadOnly ?? false) || this.isPrefilledDisplay()
  }

  private getRejectionValue(): IRejectionValue | undefined {
    const { rejectionPath, frames } = this.props
    if (!rejectionPath || !frames) return undefined
    return frames.getValue(rejectionPath) as IRejectionValue | undefined
  }

  private getEffectiveErrors(): any[] | undefined {
    const { errors, isRequired, isReadOnly, isDisabled, value } = this.props
    const filtered = filterUiErrors(errors, { isRequired, isReadOnly, isDisabled })
    let result = filtered
    // When a parent UI2 form section requests it (typically after a failed
    // wizard Next), synthesize the `is required` error for empty-required
    // fields so the shell paints red without waiting for the entity-level
    // hasSubmitted flip.
    if (
      this.showRequiredErrors &&
      isRequiredEmpty({ isRequired, isReadOnly, isDisabled, value, isValueEmpty: this.resolveIsEmpty() })
    ) {
      const alreadyPresent = (result ?? []).some((entry: any) => {
        const message = typeof entry === 'string' ? entry : entry?.detail || entry?.message
        return message === IS_REQUIRED_UI_ERROR
      })
      if (!alreadyPresent) result = [IS_REQUIRED_UI_ERROR, ...(result ?? [])]
    }
    // Synthesize a rejection error when the field value hasn't changed from
    // the rejected original. The error clears once the user provides a new value.
    if (!isReadOnly && !isDisabled) {
      const rejection = this.getRejectionValue()
      if (rejection?.status === 'rejected') {
        const isEmpty = this.resolveIsEmpty()
        const isUnchanged = isEmpty(value) || _.isEqual(value, rejection.originalValue)
        if (isUnchanged) {
          const rejectionError = rejection.reason || IS_REJECTED_UI_ERROR
          const alreadyPresent = (result ?? []).some((entry: any) => {
            const message = typeof entry === 'string' ? entry : entry?.detail || entry?.message
            return message === rejectionError
          })
          if (!alreadyPresent) result = [...(result ?? []), rejectionError]
        }
      }
    }
    return result
  }

  private getErrorText(): string | undefined {
    const errors = this.getEffectiveErrors()
    if (_.isEmpty(errors)) return undefined
    // Errors can arrive as a flat list (`['is required']`) or a nested list
    // (`[['is required']]`) — the entity renderer's `buildErrorTree` groups
    // per-path values into a list of lists at `_errors`. Peel one level of
    // wrapping so the message beneath is always what we render.
    const first = _.isArray(errors![0]) ? (errors![0] as any[])[0] : errors![0]
    return _.isString(first) ? first : (first?.detail || first?.message)
  }

  private getFieldPath(): string | undefined {
    return resolveBoundFieldPath(this.props)
  }

  private handleEdit = (): void => {
    this.setState(s => ({ prefilled: beginPrefilledEdit(s.prefilled) }))
  }

  private handleCancel = (): void => {
    this.props.onChange?.(this.state.prefilled.originalValue as any)
    this.setState(s => ({ prefilled: cancelPrefilledEdit(s.prefilled) }))
  }

  private getCancelNode(): React.ReactNode | undefined {
    const { enablePrefilledMode, isDisabled, isReadOnly, prefilledCancelText } = this.props
    const { prefilled } = this.state
    const showCancel =
      prefilled.isEditing && !!enablePrefilledMode &&
      !isDisabled && !isReadOnly && prefilled.originalValue !== undefined
    if (!showCancel) return undefined
    return (
      <UI2PrefilledCancelButton
        className='ui2-fieldShell-prefilledButton ui2-fieldShell-prefilledButton--cancel'
        onPress={this.handleCancel}
        text={prefilledCancelText}
      />
    )
  }

  /**
   * Which of the three mutually exclusive channels gets the Cancel button.
   * Exactly one is live at a time, so no combination of props can produce
   * two Cancels:
   *
   *   'context'  — `bodyIsFieldControl`: UI2FieldControl slots it beside
   *                the input.
   *   'editable' — `renderEditable`: the field places `extras.cancelNode`
   *                itself.
   *   'shell'    — neither: the shell's own row renders it under the body.
   *
   * A field that wraps its body in UI2FieldControl but forgets the flag
   * lands in 'shell' — wrong placement, never a duplicate.
   */
  private cancelChannel(): 'context' | 'editable' | 'shell' {
    if (this.props.bodyIsFieldControl) return 'context'
    if (this.props.renderEditable) return 'editable'
    return 'shell'
  }

  /**
   * Where horizontal-layout inline help goes. Mirrors `cancelChannel()`:
   * a UI2FieldControl body takes it in its footer (inside the border), and
   * everything else falls back to the shell's below-row. Before this, help
   * was pushed onto the context unconditionally — so fields without a
   * control (api, signature, geolocation, entity-search, review) had
   * nothing to render it and simply dropped their inline help.
   */
  private getErrorPrefix(): string {
    return this.getRejectionValue()?.status === 'rejected'
      ? 'Rejected for the following reason: '
      : 'Error: '
  }

  private showsHelpInBelowRow(): boolean {
    const { isHorizontalLayout, inlineHelpText, bodyIsFieldControl } = this.props
    if (!isHorizontalLayout || _.isEmpty(inlineHelpText)) return false
    // Read-only bodies have no control to hold a footer either way.
    return !bodyIsFieldControl || this.getEffectiveReadOnly()
  }

  private getContextValue(): IFieldShellContext {
    const { isDisabled, isHorizontalLayout, inlineHelpText } = this.props
    const footerContent = (isHorizontalLayout && !_.isEmpty(inlineHelpText) && !this.showsHelpInBelowRow())
      ? inlineHelpText
      : undefined
    return {
      isDisabled: !!isDisabled,
      isReadOnly: this.getEffectiveReadOnly(),
      hasError: !_.isEmpty(this.getEffectiveErrors()),
      errorText: this.getErrorText(),
      prefilledCancelNode: this.cancelChannel() === 'context' ? this.getCancelNode() : undefined,
      footerContent,
    }
  }

  private getStateClasses(): string {
    return classNames({
      'ui2-fieldShell--disabled': this.props.isDisabled,
      'ui2-fieldShell--readOnly': this.getEffectiveReadOnly(),
      'ui2-fieldShell--error': !_.isEmpty(this.getEffectiveErrors()),
      'ui2-fieldShell--prefilledEditing':
        this.state.prefilled.isEditing && this.state.prefilled.originalValue !== undefined,
    })
  }

  public render(): React.ReactNode {
    const { isHorizontalLayout } = this.props
    return (
      <UI2FormSectionIdContext.Consumer>
        {(sectionId) => (
          <UI2WizardContext.Consumer>
            {(wizardCtx) => {
              const changed = sectionId !== this.currentSectionId
              this.currentSectionId = sectionId
              this.currentWizardCtx = wizardCtx as IUI2WizardContext
              if (changed && this.registeredPath && this.context?.register) {
                this.context.register(this.registeredPath, {
                  getRef: () => this.outerRef.current,
                  pulse: (intent) => this.getPulseTarget()?.pulse(intent),
                  hasError: () => !_.isEmpty(this.getEffectiveErrors()),
                  sectionId,
                  revealSection: this.revealSection,
                })
              }
              return (
                <UI2FormSectionSubmitContext.Consumer>
                  {(showRequiredErrors) => {
                    this.showRequiredErrors = showRequiredErrors
                    return isHorizontalLayout ? this.renderHorizontal() : this.renderVertical()
                  }}
                </UI2FormSectionSubmitContext.Consumer>
              )
            }}
          </UI2WizardContext.Consumer>
        )}
      </UI2FormSectionIdContext.Consumer>
    )
  }

  private renderVertical(): React.ReactNode {
    const { label, tooltip: tooltipText, isRequired, isOptional, inlineHelpText, translationTable, frames, className, style } = this.props
    return (
      <div
        ref={this.outerRef}
        className={classNames('ui2-fieldShell', this.getStateClasses(), className)}
        style={style}
        data-field-path={this.getFieldPath()}
      >
        <UI2Label
          labelText={label}
          tooltipText={tooltipText}
          isRequired={isRequired}
          isOptional={isOptional}
          inlineHelpText={inlineHelpText}
          translationTable={translationTable}
          frames={frames}
        />
        {this.renderBody()}
        {this.renderErrorText()}
      </div>
    )
  }

  private renderHorizontal(): React.ReactNode {
    const { label, tooltip: tooltipText, isRequired, isOptional, inlineHelpText, translationTable, frames, className, style } = this.props
    const errorText = this.getErrorText()
    const showHelpInBelowRow = this.showsHelpInBelowRow()
    const hasBelowRow = !!errorText || showHelpInBelowRow
    return (
      <div
        ref={this.outerRef}
        className={classNames('ui2-fieldShell', 'ui2-fieldShell--horizontal', this.getStateClasses(), className)}
        style={style}
        data-field-path={this.getFieldPath()}
      >
        <UI2Label
          labelText={label}
          tooltipText={tooltipText}
          isRequired={isRequired}
          isOptional={isOptional}
          translationTable={translationTable}
          frames={frames}
          className='ui2-fieldShell-labelColumn'
        />
        <div className='ui2-fieldShell-controlSlot'>
          {this.renderBody()}
        </div>
        {hasBelowRow && (
          <div className='ui2-fieldShell-belowRow'>
            {errorText && <div className='ui2-fieldShell-errorText'>{this.getErrorPrefix()}{errorText}</div>}
            {showHelpInBelowRow && <div className='ui2-fieldShell-inlineHelp'>{inlineHelpText}</div>}
          </div>
        )}
      </div>
    )
  }

  /**
   * The ring the focus registry pulses. A field that renders its own
   * PulseHighlight around the part that should light up passes that ref in
   * as `pulseRef`; everything else uses the shell's body-wrapping ring.
   */
  private getPulseTarget(): IUI2PulseHighlightRef | null | undefined {
    return (this.props.pulseRef?.current as IUI2PulseHighlightRef | null) ?? this.pulseRef.current
  }

  private renderBody(): React.ReactNode {
    const isReadOnly = this.getEffectiveReadOnly()
    const inner = isReadOnly
      ? this.renderReadOnlyBody()
      : (
        <FieldShellContextProvider value={this.getContextValue()}>
          {this.renderEditableBody()}
        </FieldShellContextProvider>
      )
    // A field supplying its own ring owns the whole treatment — wrapping
    // again here would draw two rings around the same control. Fields
    // render that ring in their editable body only, so read-only still
    // falls back to the shell's own ring and stays pulseable.
    const body = (this.props.pulseRef && !isReadOnly)
      ? inner
      // Pulse wraps ONLY the input body — label / tooltip / help stay outside.
      : <UI2PulseHighlight ref={this.pulseRef}>{inner}</UI2PulseHighlight>
    return this.withLoadingOverlay(body)
  }

  /**
   * The scrim sits over the body and leaves it mounted, so a field that is
   * mid-ingest keeps its size and the layout doesn't jump when the work
   * lands. The scrim itself swallows pointer events, so the controls
   * underneath are unreachable while it is up.
   */
  private withLoadingOverlay(body: React.ReactNode): React.ReactNode {
    const { isLoading, loadingMessage } = this.props
    if (!isLoading) return body
    return (
      <div className='ui2-fieldShell-loadingHost' aria-busy='true'>
        {body}
        <div className='ui2-fieldShell-loadingOverlay'>
          <Spinner size={20} />
          {loadingMessage && (
            <span className='ui2-fieldShell-loadingMessage'>{loadingMessage}</span>
          )}
        </div>
      </div>
    )
  }

  private renderEditableBody(): React.ReactNode {
    const { children, renderEditable } = this.props
    const channel = this.cancelChannel()
    // Only the owning channel is handed a node — see cancelChannel().
    if (renderEditable) {
      const extras: IFieldShellEditableExtras = {
        cancelNode: channel === 'editable' ? this.getCancelNode() : undefined,
      }
      return renderEditable(children, extras) as React.ReactNode
    }
    const cancelNode = channel === 'shell' ? this.getCancelNode() : undefined
    if (!cancelNode) return children as React.ReactNode
    return (
      <div className='ui2-fieldShell-editableRow'>
        <div className='ui2-fieldShell-editableRow-body'>{children as React.ReactNode}</div>
        {cancelNode}
      </div>
    )
  }

  /**
   * The empty read-only state. Owned by the shell rather than by each
   * field, because a field that supplies `renderReadOnly` and forgets the
   * empty case renders nothing at all. `renderEmpty` handles the rich
   * cases; `emptyText` handles the common one (domain wording).
   */
  private renderEmptyState(): React.ReactNode {
    const { renderEmpty, emptyText } = this.props
    if (renderEmpty) return renderEmpty() as React.ReactNode
    return <UI2NoValueAvailable text={emptyText} />
  }

  private renderReadOnlyBody(): React.ReactNode {
    const {
      value, renderReadOnly, enablePrefilledMode, isDisabled, isReadOnly,
      suppressDefaultPrefilledEdit, suppressDefaultEmptyState,
    } = this.props
    const isPrefilledDisplay = this.isPrefilledDisplay()
    const isEmpty = this.resolveIsEmpty()
    const useEmptyState = isEmpty(value) && !suppressDefaultEmptyState
    const showEditButton =
      isPrefilledDisplay && !!enablePrefilledMode && !isDisabled && !isReadOnly && !suppressDefaultPrefilledEdit
    const editNode: React.ReactNode = showEditButton ? (
      <UI2PrefilledEditButton
        className='ui2-fieldShell-prefilledButton'
        onPress={this.handleEdit}
        text={this.props.prefilledEditText}
      />
    ) : undefined
    // When a custom renderReadOnly is provided, it owns button placement.
    // The shell wraps in the readOnly row but never adds the default button.
    if (!useEmptyState && renderReadOnly) {
      return (
        <div className='ui2-fieldShell-readOnly'>
          {renderReadOnly(value, { isPrefilledDisplay, onEdit: this.handleEdit, editNode }) as React.ReactNode}
        </div>
      )
    }
    const content = useEmptyState
      ? this.renderEmptyState()
      : <span>{String(value)}</span>
    return (
      <div className='ui2-fieldShell-readOnly'>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </div>
    )
  }

  private renderErrorText(): React.ReactNode {
    const errorText = this.getErrorText()
    if (!errorText) return null
    return <div className='ui2-fieldShell-errorText'>{this.getErrorPrefix()}{errorText}</div>
  }
}
